// src/pages/Enseignants/Events/EventDetailsModalEnseignants.jsx
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaTimes,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaLayerGroup,
  FaFlag,
  FaUsers,
  FaClock,
  FaExternalLinkAlt,
  FaUserTie,
  FaUserCheck,
  FaRoute,
} from "react-icons/fa";
import {
  getAdherentsByEvent,
  getCoachesByEvent,
  toggleAdherentValidation,
} from "./../../../services/eventServices";
import { useUser } from "./../../Home/UserContext";

// ✅ helper pour absolutiser /uploads/…
import { toAbsoluteEventPhotoUrl } from "../../../services/eventPhotoServices";
import getCategory from "../../../utils/getCategory";

/** Mini switch pour (dé)valider une présence */
function ValidationSwitch({ checked, onChange, disabled, title }) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      role="switch"
      aria-checked={!!checked}
      title={title || (checked ? "Validé" : "Non validé")}
      className={`relative inline-flex h-5 w-10 items-center rounded-full transition
        ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
        ${checked ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition
          ${checked ? "translate-x-5" : "translate-x-1"}`}
      />
    </button>
  );
}

const CATEGORY_ORDER = [
  "Eveil",
  "Mini-Poussin(e)",
  "Poussin(e)",
  "Benjamin(e)",
  "Minime",
  "Cadet(te)",
  "Junior",
  "Sénior",
  "Vétéran",
  "Inconnu",
];

const getAdherentBirthDate = (adherent) => {
  return (
    adherent?.date_naissance ||
    adherent?.dateNaissance ||
    adherent?.birth_date ||
    adherent?.date_of_birth ||
    adherent?.dateOfBirth ||
    adherent?.naissance ||
    null
  );
};

const getBirthYear = (adherent) => {
  const birthDate = getAdherentBirthDate(adherent);

  if (!birthDate) return null;

  const date = new Date(birthDate);

  if (Number.isNaN(date.getTime())) return null;

  return date.getFullYear();
};

const getBirthTime = (adherent) => {
  const birthDate = getAdherentBirthDate(adherent);

  if (!birthDate) return Number.POSITIVE_INFINITY;

  const date = new Date(birthDate);

  if (Number.isNaN(date.getTime())) return Number.POSITIVE_INFINITY;

  return date.getTime();
};

const getAdherentAgeCategory = (adherent) => {
  const birthDate = getAdherentBirthDate(adherent);
  const computedCategory = getCategory(birthDate);

  if (computedCategory && computedCategory !== "Inconnu") {
    return computedCategory;
  }

  return adherent?.categorie || "Inconnu";
};

const sortAdherentsByBirthThenName = (a, b) => {
  const birthDiff = getBirthTime(a) - getBirthTime(b);

  if (birthDiff !== 0) return birthDiff;

  const nameA = `${a?.nom || ""} ${a?.prenom || ""}`;
  const nameB = `${b?.nom || ""} ${b?.prenom || ""}`;

  return nameA.localeCompare(nameB, "fr", {
    sensitivity: "base",
  });
};

const sortYearGroups = (a, b) => {
  if (a.year === "Année inconnue") return 1;
  if (b.year === "Année inconnue") return -1;

  return Number(a.year) - Number(b.year);
};

const EventDetailsModalEnseignants = ({ event, isOpen, onClose }) => {
  const [adherents, setAdherents] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(false);

  // 🖼️ preview plein écran
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState("");

  const { user } = useUser();
  const userRole = user?.role_id; // 1 = Admin, 3 = Enseignant (à adapter si besoin)
  const canValidate = userRole === 1 || userRole === 3;

  const groupedAdherents = useMemo(() => {
    const categoryGroups = new Map();

    adherents.forEach((adherent) => {
      const category = getAdherentAgeCategory(adherent);
      const year = getBirthYear(adherent) || "Année inconnue";

      if (!categoryGroups.has(category)) {
        categoryGroups.set(category, new Map());
      }

      const yearGroups = categoryGroups.get(category);

      if (!yearGroups.has(year)) {
        yearGroups.set(year, []);
      }

      yearGroups.get(year).push(adherent);
    });

    return Array.from(categoryGroups.entries())
      .map(([category, yearMap]) => {
        const years = Array.from(yearMap.entries())
          .map(([year, items]) => ({
            year,
            items: [...items].sort(sortAdherentsByBirthThenName),
          }))
          .sort(sortYearGroups);

        const total = years.reduce((sum, group) => sum + group.items.length, 0);

        return {
          category,
          total,
          years,
        };
      })
      .sort((a, b) => {
        const indexA = CATEGORY_ORDER.indexOf(a.category);
        const indexB = CATEGORY_ORDER.indexOf(b.category);

        const safeIndexA = indexA === -1 ? 999 : indexA;
        const safeIndexB = indexB === -1 ? 999 : indexB;

        return safeIndexA - safeIndexB;
      });
  }, [adherents]);

  useEffect(() => {
    const load = async () => {
      if (!event) return;
      try {
        setLoading(true);
        const [adherentsData, coachesData] = await Promise.all([
          getAdherentsByEvent(event.id),
          getCoachesByEvent(event.id),
        ]);

        // Éviter les doublons (un coach ne doit pas apparaître aussi comme adhérent)
        const filteredAdherents =
          Array.isArray(adherentsData) && Array.isArray(coachesData)
            ? adherentsData.filter(
                (a) => !coachesData.some((c) => c.id === a.id),
              )
            : Array.isArray(adherentsData)
              ? adherentsData
              : [];

        setAdherents(filteredAdherents || []);
        setCoaches(Array.isArray(coachesData) ? coachesData : []);
      } catch (e) {
        console.error("Erreur chargement participants:", e);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) load();
  }, [event, isOpen]);

  // Bloque le scroll du fond quand la modale est ouverte
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const safeDate = (d) => {
    const dt = d ? new Date(d) : null;
    return dt && !isNaN(dt.valueOf()) ? dt : null;
  };
  const formatFR = (d) =>
    d
      ? d.toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "—";

  // Helper : récupère l'id adhérent fiable depuis l'objet (coach ou adhérent)
  const getAdherentIdFrom = (p) =>
    p?.AdherentEvent?.adherent_id ?? p?.adherent_id ?? p?.id ?? null;

  const handleToggleValidation = async (person, isCoach = false) => {
    if (!event?.id) return;
    const adherentId = getAdherentIdFrom(person);
    if (!adherentId) {
      console.warn(
        "[toggle] Impossible de déterminer adherentId pour :",
        person,
      );
      return;
    }

    try {
      await toggleAdherentValidation(adherentId, event.id);

      const flip = (x) => ({
        ...x,
        AdherentEvent: {
          ...(x.AdherentEvent || {}),
          is_validate: !x?.AdherentEvent?.is_validate,
        },
      });

      if (isCoach) {
        setCoaches((prev) =>
          prev.map((c) => (getAdherentIdFrom(c) === adherentId ? flip(c) : c)),
        );
      } else {
        setAdherents((prev) =>
          prev.map((a) => (getAdherentIdFrom(a) === adherentId ? flip(a) : a)),
        );
      }
    } catch (e) {
      console.error("Erreur lors du changement de validation :", e);
    }
  };

  if (!isOpen || !event) return null;

  const start = safeDate(event.start);
  const end = safeDate(event.end);

  // URL image (URL absolue, /uploads, data:, blob:, etc.)
  const imgSrc =
    toAbsoluteEventPhotoUrl(event.photo_url) || "/img/no-image.jpg";

  // Itinéraire Google Maps vers le lieu de l'événement
  const googleMapsDirectionsUrl = event.lieu_event
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        event.lieu_event,
      )}`
    : null;

  // --- Rendu plein écran via portal ---
  return createPortal(
    <div className="fixed inset-0 z-[1000]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      {/* Conteneur plein écran */}
      <div className="absolute inset-0 flex items-stretch justify-center">
        {/* Panel plein écran */}
        <div
          className="relative w-screen h-screen overflow-y-auto bg-white/90 dark:bg-slate-900/80"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-5 sm:px-7 py-4 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 text-white">
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-semibold truncate">
                {event.titre || "Détail de l’événement"}
              </h3>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs sm:text-[13px] opacity-95">
                {start && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5">
                    <FaCalendarAlt /> {formatFR(start)}
                    {end && ` → ${formatFR(end)}`}
                  </span>
                )}
                {event.lieu_event && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5">
                    <FaMapMarkerAlt /> {event.lieu_event}
                  </span>
                )}
                {event.type_event && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5">
                    <FaLayerGroup /> {event.type_event}
                  </span>
                )}
                {event.level_event && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5">
                    <FaFlag /> {event.level_event}
                  </span>
                )}
              </div>
            </div>

            <button
              onClick={onClose}
              className="shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/20 hover:bg-white/30 transition"
              aria-label="Fermer"
              title="Fermer"
            >
              <FaTimes />
            </button>
          </div>

          {/* Corps modal : grille responsive */}
          <div className="grid md:grid-cols-3 gap-0">
            {/* Colonne gauche : scrollable */}
            <div className="md:col-span-2 p-5 sm:p-7 md:max-h-none">
              {/* Bloc description & infos complémentaires */}
              <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/80 dark:bg-slate-900/60 p-4">
                {event.description && (
                  <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-line">
                    {event.description}
                  </p>
                )}

                {event.cate_event && (
                  <div className="mt-3 text-sm">
                    <span className="font-semibold text-slate-800 dark:text-slate-100">
                      Catégories :{" "}
                    </span>
                    <span className="text-slate-700 dark:text-slate-200">
                      {event.cate_event}
                    </span>
                  </div>
                )}

                {event.horaire_event && (
                  <div className="mt-2 text-sm">
                    <span className="inline-flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-100">
                      <FaClock /> Horaires :
                    </span>
                    <div
                      className="prose prose-sm dark:prose-invert max-w-none mt-1 text-slate-700 dark:text-slate-200"
                      dangerouslySetInnerHTML={{ __html: event.horaire_event }}
                    />
                  </div>
                )}

                {event.agenda_url && (
                  <div className="mt-3">
                    <a
                      href={event.agenda_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm underline decoration-dotted text-slate-900 dark:text-slate-100 hover:text-violet-500"
                    >
                      Ajouter à votre agenda{" "}
                      <FaExternalLinkAlt className="opacity-80" />
                    </a>
                  </div>
                )}
              </div>

              {/* Séparateur */}
              <div className="my-5 border-t border-slate-200 dark:border-slate-800" />

              {/* Coaches */}
              <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/80 dark:bg-slate-900/60 p-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-base font-semibold flex items-center gap-2">
                    <FaUserTie className="text-slate-500" /> Coachs inscrits
                  </h4>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
                    {coaches.length}
                  </span>
                </div>

                <div className="mt-3 divide-y divide-slate-200 dark:divide-slate-800">
                  {loading ? (
                    <div className="py-2 text-sm opacity-70">Chargement…</div>
                  ) : coaches.length === 0 ? (
                    <div className="py-2 text-sm opacity-70">
                      Aucun coach inscrit.
                    </div>
                  ) : (
                    coaches.map((coach) => {
                      const isValid = !!coach?.AdherentEvent?.is_validate;
                      return (
                        <div
                          key={coach.id}
                          className="py-2 flex items-center gap-3"
                        >
                          <div className="h-9 w-9 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center text-[12px] text-slate-600 dark:text-slate-300 ring-1 ring-slate-300 dark:ring-slate-700">
                            {String(coach.prenom || "?").charAt(0)}
                            {String(coach.nom || "?").charAt(0)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-slate-900 dark:text-slate-100 truncate">
                              {(coach.nom || "").toUpperCase()}{" "}
                              {coach.prenom || ""}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              Coach
                            </div>
                          </div>

                          {/* (Optionnel) switch coach
                          <ValidationSwitch
                            checked={isValid}
                            onChange={() => handleToggleValidation(coach, true)}
                            disabled={!canValidate}
                            title={isValid ? "Coach validé" : "Coach non validé"}
                          /> */}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Séparateur */}
              <div className="my-5 border-t border-slate-200 dark:border-slate-800" />

              {/* Adhérents groupés par catégorie d'âge et année */}
              <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/80 dark:bg-slate-900/60 p-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-base font-semibold flex items-center gap-2">
                    <FaUsers className="text-slate-500" /> Adhérents inscrits
                  </h4>

                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
                    {adherents.length}
                  </span>
                </div>

                <div className="mt-3">
                  {loading ? (
                    <div className="py-2 text-sm opacity-70">Chargement…</div>
                  ) : adherents.length === 0 ? (
                    <div className="py-2 text-sm opacity-70">
                      Aucun adhérent inscrit.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {groupedAdherents.map(({ category, total, years }) => (
                        <section
                          key={category}
                          className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/80 dark:border-slate-800 dark:bg-slate-950/50"
                        >
                          <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-900/70">
                            <div>
                              <p className="text-sm font-bold text-slate-900 dark:text-slate-100">
                                {category}
                              </p>

                              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                Catégorie d’âge
                              </p>
                            </div>

                            <span className="inline-flex items-center rounded-full bg-[#013E8E] px-2.5 py-1 text-[11px] font-bold text-white">
                              {total}
                            </span>
                          </div>

                          <div className="divide-y divide-slate-200 dark:divide-slate-800">
                            {years.map(({ year, items }) => (
                              <div key={`${category}-${year}`}>
                                <div className="flex items-center justify-between bg-slate-100/80 px-3 py-2 dark:bg-slate-900/80">
                                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                                    {year}
                                  </p>

                                  <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-700">
                                    {items.length}
                                  </span>
                                </div>

                                <div className="divide-y divide-slate-200 dark:divide-slate-800">
                                  {items.map((adh) => {
                                    const isValid =
                                      !!adh?.AdherentEvent?.is_validate;
                                    const birthYear = getBirthYear(adh);

                                    return (
                                      <div
                                        key={adh.id}
                                        className="flex items-center gap-3 px-3 py-2.5"
                                      >
                                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[12px] text-slate-600 ring-1 ring-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
                                          {String(adh.prenom || "?").charAt(0)}
                                          {String(adh.nom || "?").charAt(0)}
                                        </div>

                                        <div className="min-w-0 flex-1">
                                          <div className="truncate font-medium text-slate-900 dark:text-slate-100">
                                            {(adh.nom || "").toUpperCase()}{" "}
                                            {adh.prenom || ""}{" "}
                                            {birthYear && (
                                              <span className="font-normal text-slate-500 dark:text-slate-400">
                                                ({birthYear})
                                              </span>
                                            )}
                                          </div>

                                          {adh.categorie &&
                                            adh.categorie !== category && (
                                              <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                                                {adh.categorie}
                                              </div>
                                            )}
                                        </div>

                                        <ValidationSwitch
                                          checked={isValid}
                                          onChange={() =>
                                            handleToggleValidation(adh, false)
                                          }
                                          disabled={!canValidate}
                                          title={
                                            isValid
                                              ? "Inscription validée"
                                              : "Inscription non validée"
                                          }
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </section>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-3 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                  <FaUserCheck />{" "}
                  {canValidate
                    ? "Cliquez sur le switch pour valider / dévalider la présence."
                    : "Lecture seule : vous ne pouvez pas modifier les validations."}
                </div>
              </div>
            </div>

            {/* Colonne droite : image + méta compactes */}
            <aside className="md:col-span-1 p-5 sm:p-7 bg-slate-50/60 dark:bg-slate-900/40 border-l border-slate-200 dark:border-slate-800 self-start">
              <div className="rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 bg-white/80 dark:bg-slate-900/60">
                <div className="p-3">
                  <div className="w-full">
                    <img
                      src={imgSrc}
                      alt={event.titre || "Illustration"}
                      className="block w-full max-h-[42vh] object-contain rounded-md cursor-zoom-in"
                      onClick={() => {
                        setPreviewSrc(imgSrc);
                        setIsPreviewOpen(true);
                      }}
                      onError={(e) =>
                        (e.currentTarget.src = "/img/no-image.jpg")
                      }
                    />
                  </div>
                </div>
                <div className="px-4 pb-4 text-sm text-slate-700 dark:text-slate-200 space-y-2">
                  {event.type_event && (
                    <div className="flex items-center gap-2">
                      <FaLayerGroup className="opacity-70" />
                      <span className="truncate">{event.type_event}</span>
                    </div>
                  )}
                  {event.level_event && (
                    <div className="flex items-center gap-2">
                      <FaFlag className="opacity-70" />
                      <span className="truncate">{event.level_event}</span>
                    </div>
                  )}
                  {event.lieu_event && (
                    <div className="rounded-lg bg-slate-50 p-3 ring-1 ring-slate-200 dark:bg-slate-800/60 dark:ring-slate-700">
                      <div className="flex items-start gap-2">
                        <FaMapMarkerAlt className="mt-0.5 shrink-0 text-rose-500" />

                        <span className="min-w-0 text-sm text-slate-700 dark:text-slate-200">
                          {event.lieu_event}
                        </span>
                      </div>

                      <a
                        href={googleMapsDirectionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
                        title={`Afficher l’itinéraire vers ${event.lieu_event}`}
                        aria-label={`Afficher l’itinéraire Google Maps vers ${event.lieu_event}`}
                      >
                        <FaRoute />
                        Voir le trajet sur Google Maps
                        <FaExternalLinkAlt className="text-[10px] opacity-80" />
                      </a>
                    </div>
                  )}
                  {event.cate_event && (
                    <div className="flex items-center gap-2">
                      <FaUsers className="opacity-70" />
                      <span className="truncate">{event.cate_event}</span>
                    </div>
                  )}
                  {start && (
                    <div className="flex items-center gap-2">
                      <FaCalendarAlt className="opacity-70" />
                      <span className="truncate">{formatFR(start)}</span>
                    </div>
                  )}
                  {end && (
                    <div className="flex items-center gap-2">
                      <FaCalendarAlt className="opacity-70" />
                      <span className="truncate">{formatFR(end)}</span>
                    </div>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {/* 🖼️ Preview image plein écran */}
      {isPreviewOpen && (
        <div
          className="fixed inset-0 z-[1100] bg-black/90 flex items-center justify-center p-4"
          onClick={(e) =>
            e.currentTarget === e.target && setIsPreviewOpen(false)
          }
          role="dialog"
          aria-modal="true"
          aria-label="Aperçu de l'image"
        >
          <button
            className="absolute top-4 right-4 h-10 w-10 grid place-items-center rounded-full bg-white/15 hover:bg-white/25 text-white"
            onClick={() => setIsPreviewOpen(false)}
            title="Fermer"
          >
            <FaTimes />
          </button>
          <img
            src={previewSrc}
            alt="Aperçu événement"
            className="max-h-[92vh] max-w-[96vw] object-contain"
            onError={(e) => (e.currentTarget.src = "/img/no-image.jpg")}
          />
        </div>
      )}
    </div>,
    document.body,
  );
};

export default EventDetailsModalEnseignants;
